import React, { useEffect, useState } from 'react';
import { Box, Flex, TextV2, ButtonV2, DialogV2, DrawerV2, CloseButton } from '@withjoy/joykit';
import { ReactComponent as StarFilled } from '@assets/icons/star-filled.svg';
import { Link } from '@withjoy/joykit/icons';
import { pxToRem } from '@shared/joykit/packages/core/common';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useTranslation } from '@shared/core';
import { ThingToDoComponentProps, ThingToDoTile, useThingToDo, useAspectRatioHeight } from '.';
import { closeButtonOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body3ToButtonsOverride,
  hed4ToSubHeadingOverride,
  hed6ToSubHeadingOverride,
  label2ToParagraphOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useThingsToDoTelemetry } from './ThingsToDo.telemetry';

export const ThingToDoModal: React.FC<ThingToDoComponentProps> = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <ThingToDoTile
        onClick={handleOpenModal}
        {...props}
      />
      <ThingToDoModalContent
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        {...props}
      />
    </>
  );
};

interface ThingToDoModalContentProps extends ThingToDoComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThingToDoModalContent: React.FC<ThingToDoModalContentProps> = ({ isOpen, onClose, ...props }) => {
  const isMobile = useIsMobileScreen();
  const { t } = useTranslation('guestSiteCustom');
  const { constructCampaignUrl } = useThingToDo();
  const { formatCurrency } = useCurrencyFormatter();
  const { elementRef: tourImageRef, calculatedHeight } = useAspectRatioHeight(2.1, 148);
  const telemetry = useThingsToDoTelemetry();

  useEffect(() => {
    if (isOpen) {
      telemetry.thingsToDoLayoutsExperiment.enter();
    }
  }, [isOpen, telemetry]);

  const renderModalContent = () => (
    <Box
      position="relative"
      height="100%"
    >
      <Box
        position="absolute"
        top={pxToRem(16)}
        right={pxToRem(16)}
        zIndex={1}
      >
        <CloseButton
          overrides={closeButtonOverrides}
          onClick={onClose}
          aria-label={t('thingsToDo', 'closeModal')()}
        />
      </Box>
      <Box>
        {props.image?.url && (
          <Box
            width="100%"
            height={pxToRem(395)}
            overflow="hidden"
          >
            <img
              src={props.image.url}
              alt={props.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        <Box padding={6}>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2
              typographyVariant="hed6"
              marginBottom={5}
              fontWeight={'semibold'}
            >
              {props.title}
            </TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body3ToButtonsOverride}>
            <TextV2
              typographyVariant="body3"
              color="mono12"
            >
              {props.description}
            </TextV2>
          </GuestSiteTypographyOverride>
        </Box>
        <Box padding={6}>
          <Box
            display="grid"
            gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'}
            gap={6}
          >
            {props.tours?.map(tour => (
              <Flex
                as="a"
                href={constructCampaignUrl(tour.url)}
                target="_blank"
                rel="noopener"
                key={tour.id}
                borderRadius={12}
                cursor="pointer"
                _hover={{ borderColor: 'mono4' }}
                minHeight={pxToRem(439)}
                flexDirection="column"
                onClick={() => {
                  telemetry.thingsToDoTourClicked(tour.url, {
                    componentName: 'ThingToDoModal',
                    url: tour.url,
                    thingToDoName: props.title,
                    city: props.location,
                  });
                }}
                boxShadow="0 3px 8px 0 rgba(0, 0, 0, 0.03), 0 10px 60px -20px rgba(44, 41, 37, 0.03), 0 9px 36px -30px rgba(0, 0, 0, 0.04)"
              >
                {tour.image && (
                  <Box
                    ref={tourImageRef}
                    width="100%"
                    height={pxToRem(calculatedHeight)}
                    overflow="hidden"
                    borderTopLeftRadius={12}
                    borderTopRightRadius={12}
                  >
                    <img
                      src={tour.image.url}
                      alt={tour.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}
                <Flex
                  paddingX={6}
                  paddingTop={4}
                  paddingBottom={6}
                  flexDirection="column"
                  justifyContent="space-between"
                  flex="1"
                >
                  <Box>
                    <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride}>
                      <TextV2
                        typographyVariant="hed4"
                        marginBottom={pxToRem(8)}
                      >
                        {tour.title}
                      </TextV2>
                    </GuestSiteTypographyOverride>
                    <GuestSiteTypographyOverride override={label2ToParagraphOverride}>
                      <TextV2
                        typographyVariant="label2"
                        color="mono11"
                        marginBottom={3}
                      >
                        {tour.description}
                      </TextV2>
                    </GuestSiteTypographyOverride>
                  </Box>
                  <Box>
                    {tour.price && (
                      <Flex
                        justifyContent="space-between"
                        alignItems="flex-end"
                      >
                        <Box>
                          <TextV2
                            typographyVariant="label2"
                            color="mono8"
                          >
                            {t('thingsToDo', 'from')()}
                          </TextV2>
                          <TextV2
                            typographyVariant="body2"
                            color="mono8"
                          >
                            <TextV2
                              as="span"
                              fontWeight="bold"
                              color="mono14"
                              fontFamily={'Inter UI'}
                            >
                              {formatCurrency({
                                priceFloatingPointDecimalString: (tour.price.valueInMinorUnits / 100).toFixed(2),
                                priceCurrencyCode: tour.price.currencyCode,
                                formatForm: 'short',
                                formatPrecision: 0,
                              })}
                            </TextV2>{' '}
                            {t('thingsToDo', 'perPerson')()}
                          </TextV2>
                        </Box>
                        <Flex
                          alignItems="center"
                          gap={1}
                        >
                          {tour.rating && (
                            <>
                              <StarFilled />
                              <TextV2
                                typographyVariant="body2"
                                color="mono8"
                                fontFamily={'Inter UI'}
                              >
                                {tour.rating.avgRating.toFixed(1)} ({tour.rating.reviewsCount})
                              </TextV2>
                            </>
                          )}
                        </Flex>
                      </Flex>
                    )}
                  </Box>
                </Flex>
                <Box
                  paddingX={6}
                  paddingBottom={6}
                >
                  <ButtonV2
                    variant="outline"
                    size="md"
                    fullWidth
                    intent="neutral"
                    shape="rounded"
                  >
                    {t('thingsToDo', 'seeAvailability')()}
                  </ButtonV2>
                </Box>
              </Flex>
            ))}
          </Box>
        </Box>
        <Box
          marginBottom={9}
          paddingX={6}
        >
          <ButtonV2
            as="a"
            width="100%"
            href={constructCampaignUrl(props.url)}
            target="_blank"
            rel="noopener sponsored nofollow"
            variant="outline"
            intent="neutral"
            shape="rounded"
            endIcon={<Link />}
            borderColor="mono5"
            fontFamily={'Inter UI'}
            onClick={() => {
              telemetry.thingsToDoCTAClicked(props.url, {
                componentName: 'ThingToDoModal',
                url: props.url,
                thingToDoName: props.title,
                city: props.location,
                tours: props.tours,
              });
            }}
          >
            {t('thingsToDo', 'exploreMore')()}
          </ButtonV2>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={onClose}
        anchor="bottom"
        size={0.95}
        stickyBehavior="header-footer"
        overrides={{
          Content: {
            props: {
              borderRadius: '24px',
              boxShadow: '0 50px 100px -24px rgba(44, 41, 37, 0.25), 0 30px 60px -24px rgba(0, 0, 0, 0.30)',
              padding: 0,
              height: 'auto',
              maxHeight: '95vh',
              overflow: 'hidden',
            },
          },
          Body: {
            props: {
              padding: 0,
              overflowY: 'auto',
              maxHeight: '95vh',
            },
          },
        }}
      >
        <DrawerV2.Body>{renderModalContent()}</DrawerV2.Body>
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      overrides={{
        Content: {
          props: {
            padding: '0 !important',
            margin: '0 !important',
            height: 'auto',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        },
        Body: {
          props: {
            padding: '0 !important',
            margin: '0 !important',
            overflowY: 'auto',
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogV2.Body>{renderModalContent()}</DialogV2.Body>
    </DialogV2>
  );
};
